import classNames from 'classnames';
import useWindowDimensions from 'hooks/useWindowDimensions';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getFundCentreData } from '@/store/fundCentre';
import { useI18n } from 'next-localization';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import FundsThead from 'components/Pages/FundsCenter/AllFunds/FundsListing/FundsTableParts/FundsThead';
import FundsTableRow from 'components/Pages/FundsCenter/AllFunds/FundsListing/FundsTableParts/FundsTableRow';

import CustomScroll from 'components/Functional/CustomScroll';

type Column = {
  propertyName: string;
  visibleName: string;
};

type Output = {
  general: Column[];
  shareclassSpecific: Column[];
};

type OptionColumn = {
  general: Column[];
  shareclassSpecific: Column[];
};

const containerWidth = 1170;

const FundsTable: React.FC<{ isFetching: boolean }> = ({ isFetching }) => {
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const deviceType: string = useWindowDimensions();
  const tableRef = useRef(null);
  const [gradientPosition, setGradientPosition] = useState('gradient-right');
  const [showScroll, setShowScroll] = useState(false);
  const { funds, selectedTab } = useSelector(getFundCentreData);
  const hideShareclassCell =
    sitecoreContext.language === 'en-JP' || sitecoreContext.language === 'ja-JP';

  const optionalColumns: OptionColumn = useMemo(() => {
    if (selectedTab === 'prices' || selectedTab === 'navs') {
      return {
        general: [
          {
            propertyName: 'fundType',
            visibleName: t('fundsFundCentreFiltersFundType'),
          },
          {
            propertyName: 'productType',
            visibleName: t('fundsFundCentreFiltersProductType'),
          },
        ],
        shareclassSpecific: [
          {
            propertyName: 'newspaperName',
            visibleName: t('newspaperName'),
          },
          {
            propertyName: 'dailyPrice',
            visibleName: t('fundCentrePricesDailyPrice'),
          },
          {
            propertyName: 'priceChange',
            visibleName: t('fundCentrePricesPriceChange'),
          },
          {
            propertyName: 'currency',
            visibleName: t('fundCentrePricesCurrency'),
          },
          {
            propertyName: 'nav',
            visibleName: t('fundCentrePricesNav'),
          },
          {
            propertyName: 'offerPrice',
            visibleName: t('fundCentrePricesOffer'),
          },
          {
            propertyName: 'bidPrice',
            visibleName: t('fundCentrePricesBid'),
          },
          {
            propertyName: 'assetSize',
            visibleName: t('fundCentrePricesAssetSize'),
          },
          {
            propertyName: 'navPrice',
            visibleName: t('fundCentrePricesNavPrice'),
          },
          {
            propertyName: 'applicationPrice',
            visibleName: t('fundCentrePricesApplicationPrice'),
          },
          {
            propertyName: 'redemptionPrice',
            visibleName: t('fundCentrePricesRedemptionPrice'),
          },
          {
            propertyName: 'sharePrice',
            visibleName: t('fundCentrePricesSharePrice'),
          },
          {
            propertyName: 'estimatedCumFairNav',
            visibleName: t('fundCentrePricesEstimatedCumFairNav'),
          },
          {
            propertyName: 'cumFairPremiumDiscount',
            visibleName: t('fundCentrePricesCumFairPremiumDiscount'),
          },
          {
            propertyName: 'estimatedExFairNav',
            visibleName: t('fundCentrePricesEstimatedExFairNav'),
          },
          {
            propertyName: 'exFairPremiumDiscount',
            visibleName: t('fundCentrePricesExFairPremiumDiscount'),
          },
          {
            propertyName: 'navChange',
            visibleName: t('fundCentrePricesNavChange'),
          },
          {
            propertyName: 'ticker',
            visibleName: t('fundCentrePricesTicker'),
          },
        ],
      };
    }

    return {
      general: [
        {
          propertyName: 'fundType',
          visibleName: t('fundsFundCentreFiltersFundType'),
        },
        {
          propertyName: 'sfdrClassification',
          visibleName: t('sfdrClassification'),
        },
        {
          propertyName: 'assetClass',
          visibleName: t('assetClass'),
        },
        {
          propertyName: 'morningstar',
          visibleName: t('morningstar'),
        },
        {
          propertyName: 'categoryX',
          visibleName: t('categoryX'),
        },
        {
          propertyName: 'newspaperName',
          visibleName: t('newspaperName'),
        },
      ],
      shareclassSpecific: [
        {
          propertyName: 'factsheet',
          visibleName: t('factsheet'),
        },
        {
          propertyName: 'kid',
          visibleName: t('kid'),
        },
        {
          propertyName: 'kiid',
          visibleName: t('kiid'),
        },
        {
          propertyName: 'annualReport',
          visibleName: t('annualReport'),
        },
        {
          propertyName: 'interimReport',
          visibleName: t('interimReport'),
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  const columnsToShow = useMemo(() => {
    const output: Output = {
      general: [],
      shareclassSpecific: [],
    };
    if (!funds.length) return output;
    funds.forEach((fund) => {
      optionalColumns.general.forEach((column, index) => {
        if (fund.hasOwnProperty(column.propertyName) && !output.general.includes(column)) {
          output.general.splice(index, 0, column);
        }
      });
      optionalColumns.shareclassSpecific.forEach((column, index) => {
        if (
          fund.shareclasses?.[0]?.hasOwnProperty(column.propertyName) &&
          !output.shareclassSpecific.includes(column)
        ) {
          output.shareclassSpecific.splice(index, 0, column);
        }
      });
    });
    return output;
  }, [funds, optionalColumns]);

  const showSkeleton = isFetching;

  const fundsOrSkeleton: Array<any> = useMemo(() => {
    if (showSkeleton) {
      return new Array(20).fill({ skeleton: true });
    }
    return funds;
  }, [showSkeleton, funds]);

  const isScrollable: boolean = useMemo(() => {
    return selectedTab === 'prices' && showScroll && deviceType === 'desktop';
  }, [showScroll, deviceType, selectedTab]);

  const changeGradientPosition = (val) => {
    setGradientPosition(val);
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > containerWidth) {
          setShowScroll(true);
        } else {
          setShowScroll(false);
        }
      }
    });

    if (tableRef.current) {
      resizeObserver.observe(tableRef.current);
    }

    return () => {
      if (tableRef.current) {
        resizeObserver.unobserve(tableRef.current);
      }
    };
  }, []);

  return (
    <>
      <div className="position-relative" style={{ zIndex: 0 }}>
        <div
          id="wrapperId"
          className={classNames('wrapperId', {
            scrollable: isScrollable,
            [gradientPosition]: gradientPosition && isScrollable,
          })}
        >
          <table
            id="tableId"
            ref={tableRef}
            aria-label="All funds table"
            className="w-100 fundsTable"
          >
            <FundsThead
              showSkeleton={showSkeleton}
              columnsToShow={columnsToShow}
              hideShareclass={hideShareclassCell}
            />
            <tbody>
              {fundsOrSkeleton.map((fund, idx) => (
                <FundsTableRow
                  key={idx}
                  fund={fund}
                  columnsToShow={columnsToShow}
                  fundIdx={idx}
                  hideShareclass={hideShareclassCell}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isScrollable && (
        <CustomScroll
          tableId="tableId"
          wrapperId="wrapperId"
          changeGradientPosition={changeGradientPosition}
        />
      )}
    </>
  );
};

export default FundsTable;
